import React, { useState } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, CardHeader, CardTitle, Btn, Badge, Chip, SectionHead, Table, Tr, Td, InfoBanner, DataField } from '../../common/UI';
import { generateDeliveryNotePDF, generateStocklistPDF } from '../../../utils/pdfGenerator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export function MyStock({ loggedUser }) {
  const { stock } = useApp();
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [filterVessel, setFilterVessel] = useState('all');

  /* Base set of items for this customer */
  const baseStock = stock.filter(s => {
    if (loggedUser?.company) return s.customer === loggedUser.company;
    return true; // show all for demo
  });

  const vessels = [...new Set(baseStock.map(s => s.vessel))];

  const filtered = baseStock.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      (s.po || '').toLowerCase().includes(q) ||
      (s.vessel || '').toLowerCase().includes(q) ||
      (s.awb || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q);

    const matchV = filterVessel === 'all' || s.vessel === filterVessel;

    let matchTab = false;
    if (tab === 'active') matchTab = (s.status === 'active');
    else if (tab === 'completed') matchTab = (s.status === 'completed');
    else if (tab === 'offland') matchTab = (s.type === 'Offland');

    return matchQ && matchV && matchTab;
  });

  const activeCount = baseStock.filter(s => s.status === 'active').length;
  const completedCount = baseStock.filter(s => s.status === 'completed').length;
  const offlandCount = baseStock.filter(s => s.type === 'Offland').length;

  const handleExportExcel = (data) => {
    const ws = XLSX.utils.json_to_sheet(data.map(s => ({
      Date: s.date,
      Vessel: s.vessel,
      Customer: s.customer,
      PO_Number: s.po,
      AWB_BL: s.awb,
      Description: s.description,
      Qty: s.qty,
      Unit: s.unit || 'KGS',
      Weight: s.weight,
      Location: s.location || '—',
      Status: s.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock List");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `CargoFlow_MyStock_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const inputBaseStyle = { background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 11px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' };

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead title={`Stock List  ${loggedUser?.company || ''}`} subtitle="View-only access to your items by category" />
      <InfoBanner>👁 This is your read-only stock view. Contact the warehouse team for any changes.</InfoBanner>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
            width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search PO, vessel, AWB, description..."
            style={{ ...inputBaseStyle, paddingLeft: 32, width: '100%' }} />
        </div>
        <div style={{ flex: 1 }} />
        <select value={filterVessel} onChange={e => setFilterVessel(e.target.value)} style={{ ...inputBaseStyle, width: 170 }}>
          <option value="all">All Vessels</option>
          {vessels.map(v => <option key={v}>{v}</option>)}
        </select>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 16, background: 'var(--white)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 5, boxShadow: 'var(--shadow)'
      }}>
        {[
          { key: 'active', label: 'Active Stock', count: activeCount },
          { key: 'completed', label: 'Completed', count: completedCount },
          { key: 'offland', label: 'OffLand', count: offlandCount },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', transition: 'all .15s',
              background: tab === t.key ? 'var(--accent)' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
            {t.label}
            <span style={{
              padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: tab === t.key ? 'rgba(255,255,255,.25)' : 'var(--surface2)',
              color: tab === t.key ? '#fff' : 'var(--muted)'
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <Table
            headers={['Date', 'Vessel', 'Supplier', 'FRT', 'AWB/BL/LR', 'PO Number', 'Qty', 'Wgt', 'Description', 'Location', 'Status']}
            empty={filtered.length === 0 ? `No ${tab} items matches your search/filters` : null}>
            {filtered.map(s => (
              <Tr key={s.id}>
                <Td><span style={{ fontSize: 12, color: 'var(--muted)' }}>{s.date}</span></Td>
                <Td strong>{s.vessel}</Td>
                <Td><span style={{ fontSize: 12 }}>{s.supplier}</span></Td>
                <Td><Chip variant="blue">{s.frtType || s.freight || '—'}</Chip></Td>
                <Td><span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{s.awb || '—'}</span></Td>
                <Td strong><span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{s.po}</span></Td>
                <Td>{s.qty} {s.pkgType || 'PKG'}</Td>
                <Td>{s.weight} {s.unit || 'KGS'}</Td>
                <Td><span style={{ fontSize: 12 }}>{s.description || '—'}</span></Td>
                <Td>{s.location ? <Chip>{s.location}</Chip> : '—'}</Td>
                <Td><Badge status={s.status} /></Td>
              </Tr>
            ))}
          </Table>
          <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
            <Btn variant="outline" size="sm" onClick={() => handleExportExcel(filtered)}>
              📊 Export Excel
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => generateStocklistPDF(filtered)}>
              ↓ Export PDF
            </Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function MyDeliveries({ loggedUser }) {
  const { dns } = useApp();
  const [search, setSearch] = useState('');
  const [filterVessel, setFilterVessel] = useState('all');

  /* Filter by logged-in customer */
  const baseDns = dns.filter(d => {
    if (loggedUser?.company) return d.customer === loggedUser.company;
    return true; // show all for demo
  });

  const vessels = [...new Set(baseDns.map(d => d.vessel))];

  const filtered = baseDns.filter(dn => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      (dn.dnNumber || '').toLowerCase().includes(q) ||
      (dn.vessel || '').toLowerCase().includes(q) ||
      (dn.deliveredTo || '').toLowerCase().includes(q) ||
      (dn.destination || '').toLowerCase().includes(q);

    const matchV = filterVessel === 'all' || dn.vessel === filterVessel;
    return matchQ && matchV;
  });

  const inputBaseStyle = { background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 11px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' };

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead title={`Delivery Orders ${loggedUser?.company || ''}`} subtitle="View your delivery notes and download PDFs" />
      <InfoBanner>👁 Read-only. Contact warehouse for any queries regarding your deliveries.</InfoBanner>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
            width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search DN, vessel, destination..."
            style={{ ...inputBaseStyle, paddingLeft: 32, width: '100%' }} />
        </div>
        <div style={{ flex: 1 }} />
        <select value={filterVessel} onChange={e => setFilterVessel(e.target.value)} style={{ ...inputBaseStyle, width: 170 }}>
          <option value="all">All Vessels</option>
          {vessels.map(v => <option key={v}>{v}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚚</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)' }}>No deliveries found</div>
        </div>
      ) : filtered.map(dn => (
        <div key={dn.id} style={{
          border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          background: 'var(--white)', boxShadow: 'var(--shadow)', marginBottom: 14, overflow: 'hidden'
        }}>
          <div style={{
            padding: '14px 18px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 12
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: "'JetBrains Mono',monospace", fontSize: 16, fontWeight: 600,
                color: 'var(--accent)'
              }}>{dn.dnNumber}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {dn.vessel} · {dn.createdDate}
              </div>
            </div>
            <Badge status={dn.status} />
          </div>
          <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            <DataField label="Packages" value={`${dn.packages || 0} pkgs`} />
            <DataField label="Total Weight" value={`${dn.totalWeight || 0} kg`} />
            <DataField label="Delivered To" value={dn.deliveredTo} />
            <DataField label="Destination" value={dn.destination} />
          </div>

          {dn.dimensions && dn.dimensions !== '—' && (
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '.5px' }}>
                📏 Dimensions for {dn.dimensions.split(',').length} Item(s)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8 }}>
                {dn.dimensions.split(',').map((d, i) => {
                  const parts = d.trim().split(/X|x/);
                  return (
                    <div key={i} style={{ 
                      padding: '8px 12px', background: 'var(--white)', borderRadius: 8, 
                      border: '1.5px solid var(--border)', fontSize: 11.5, fontFamily: "'JetBrains Mono',monospace",
                      display: 'flex', alignItems: 'center'
                    }}>
                      <span style={{ fontWeight: 800, color: 'var(--accent)', marginRight: 10, fontSize: 10 }}>#{i+1}</span>
                      {parts.length === 3 ? (
                        <span>{parts[0]}<small>(L)</small> × {parts[1]}<small>(W)</small> × {parts[2]}<small>(H)</small> <span style={{fontSize:9, color:'var(--muted)'}}>cm</span></span>
                      ) : d.trim()}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {dn.remarks && (
            <div style={{ padding: '8px 18px 12px', fontSize: 12.5, color: 'var(--text2)', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
              <span style={{ fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '.5px' }}>Remarks: </span>
              {dn.remarks}
            </div>
          )}
          <div style={{ padding: '8px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
            <span onClick={() => generateDeliveryNotePDF(dn, dn.items)}
              style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              📥 Download {dn.doc_type || 'Delivery Note'} PDF
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
